import { useEffect, useRef } from "react";
import CodefixClient from "../services/CodefixClient";
import { useAuth } from "./core/Auth";
import { AUTH_LOCAL_STORAGE_KEY } from "./core/AuthServices";
import { getLocalStorageWithExpiry } from "../core/utils/utils";

/**
 * Subdomain SSO:
 *   - On mount: ask backend GET /me. If the .k.net.mk cookie is present
 *     (because user signed in on app.k.net.mk earlier), hydrate auth and
 *     skip the login flow.
 *   - On focus / visibility change: re-check, so remote logout propagates.
 */
export const useSsoBootstrap = () => {
  const { auth, user, saveAuth, saveUser } = useAuth();
  const checking = useRef(false);

  useEffect(() => {
    const client = new CodefixClient();

    const check = async () => {
      if (checking.current) return;
      checking.current = true;
      try {
        const me = await client.me();
        if (me) {
          if (!user) saveUser(me);
          if (!auth) {
            const existing = getLocalStorageWithExpiry(AUTH_LOCAL_STORAGE_KEY);
            if (!existing) {
              saveAuth({ user: me, token: "__sso_cookie__" } as any);
            }
          }
        } else if (auth?.token === "__sso_cookie__") {
          // SSO-only session — the .k.net.mk cookie is gone, clear and redirect.
          // Users who logged in with a real JWT keep their session independently of /me.
          saveAuth(undefined as any);
          saveUser(undefined as any);
          const path = window.location.pathname;
          if (path !== "/" && path !== "/sign-in" && path !== "/enter-password") {
            window.location.href = "/";
          }
        }
      } finally {
        checking.current = false;
      }
    };

    void check();

    const onVisibility = () => {
      if (document.visibilityState === "visible") void check();
    };
    window.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);

    return () => {
      window.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
